import { useEffect, useMemo, useRef, useState } from "react";
import { MapView } from "./components/MapView";
import { SpeedHud } from "./components/SpeedHud";
import { Player } from "./components/Player";
import { StationPicker } from "./components/StationPicker";
import { TopBar } from "./components/TopBar";
import { useGeolocation } from "./lib/geo";
import { useRoadContext } from "./lib/road";
import { useStations, useNowPlaying, type NowPlaying, type Station } from "./lib/radio";
import { useFavorites } from "./lib/favorites";
import { useAudioSource, type LiveMetadata } from "./lib/audio";
import { useWeather } from "./lib/weather";
import { useTraffic } from "./lib/traffic";
import { useTheme } from "./lib/theme";
import { useNavigation } from "./lib/nav";
import { SearchPanel } from "./components/SearchPanel";
import { NavStrip } from "./components/NavStrip";
import { ManeuverInset } from "./components/ManeuverInset";
import { DebugPanel } from "./components/DebugPanel";
import { extractSwatch, type Swatch } from "./lib/color";
import { apiUrl } from "./lib/api";
import { IS_LITE } from "./lib/perf";

export default function App() {
  // Pull the active nav route (when present) and feed it back into the
  // simulator so the demo drives along the real OSRM-computed polyline.
  // On first mount nav has no route, so the simulator runs its default
  // Copenhagen loop. As soon as the user starts navigation the simulator
  // switches to driving that route end-to-end (and loops).
  const [demoRoute, setDemoRoute] = useState<{ coordinates: [number, number][] } | null>(null);
  const rawPosition = useGeolocation({
    simulate: true,
    simulateAlong: demoRoute,
  });
  const { position, limit, error: roadError } = useRoadContext(rawPosition);
  const weather = useWeather(position);
  const traffic = useTraffic();
  const theme = useTheme(weather?.isDay ?? null);
  const stations = useStations();
  const favorites = useFavorites();
  const nav = useNavigation(position);

  // When nav has a route, drive along it; when it's cleared, stop.
  useEffect(() => {
    setDemoRoute(nav.state.route?.geometry ?? null);
  }, [nav.state.route]);

  // Demo button: one-click sample drive (Rosenborg Slot from current pos).
  const startDemoRoute = async () => {
    if (nav.state.route) { nav.cancelNavigation(); return; }
    nav.startNavigation({
      lat: 55.6857,
      lon: 12.5774,
      name: "Rosenborg Slot",
      line: "Øster Voldgade, København",
    });
  };
  const [searchOpen, setSearchOpen] = useState(() => {
    try { return new URLSearchParams(location.search).has("search"); } catch { return false; }
  });

  // Satellite/aerial basemap toggle, persisted across sessions.
  const [satellite, setSatellite] = useState(() => {
    try { return localStorage.getItem("cardash:satellite") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("cardash:satellite", satellite ? "1" : "0"); } catch {}
  }, [satellite]);

  // GPS debug overlay — toggled by tapping the fix-status pill, or via
  // ?debug=1. Persisted so it survives reloads while diagnosing.
  const [debug, setDebug] = useState(() => {
    try {
      if (new URLSearchParams(location.search).has("debug")) return true;
      return localStorage.getItem("cardash:debug") === "1";
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("cardash:debug", debug ? "1" : "0"); } catch {}
  }, [debug]);

  const [stationId, setStationId] = useState<string | null>(() => {
    try { return localStorage.getItem("cardash:last-station"); } catch { return null; }
  });
  const [playing, setPlaying] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(() => {
    try { return new URLSearchParams(location.search).has("picker"); } catch { return false; }
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const station = useMemo(() => stations.find((s) => s.id === stationId) ?? null, [stations, stationId]);
  const polledNp = useNowPlaying(stationId, playing);

  // In-band ID3 metadata from the live HLS stream — DR (and most radio
  // broadcasters) ship the current track this way, so we get real-time
  // updates without an authenticated API.
  const [liveMeta, setLiveMeta] = useState<LiveMetadata | null>(null);
  // reset live metadata whenever the station changes
  useEffect(() => { setLiveMeta(null); }, [stationId]);

  // Merge in-band metadata over polled metadata. Live wins because it's
  // updated the instant the song changes, whereas the polled feed lags.
  const np: NowPlaying | null = useMemo(() => {
    if (!liveMeta && !polledNp) return null;
    return {
      artist: liveMeta?.artist ?? polledNp?.artist ?? null,
      title: liveMeta?.title ?? polledNp?.title ?? null,
      album: liveMeta?.album ?? polledNp?.album ?? null,
      albumartUrl: polledNp?.albumartUrl ?? null,
      stream: polledNp?.stream ?? null,
      format: polledNp?.format ?? null,
      startedAt: polledNp?.startedAt,
      duration: polledNp?.duration,
      station: polledNp?.station,
    };
  }, [liveMeta, polledNp]);

  // persist last station so it resumes on next load
  useEffect(() => {
    if (stationId) try { localStorage.setItem("cardash:last-station", stationId); } catch {}
  }, [stationId]);

  // For podcasts the actual stream comes back inside the now-playing response.
  // For everything else we use the station's static stream URL.
  const resolvedUrl = np?.stream || station?.stream || null;
  const resolvedFormat = (np?.format || station?.format || null);

  useAudioSource({
    audioRef: audioRef as React.RefObject<HTMLAudioElement>,
    url: resolvedUrl,
    format: resolvedFormat,
    playing,
    kind: station?.kind,
    onLiveMetadata: setLiveMeta,
  });

  // accent color extracted from current cover art (or station accent fallback)
  const [swatch, setSwatch] = useState<Swatch | null>(null);
  const coverUrl = np?.albumartUrl || station?.art || null;
  useEffect(() => {
    if (!coverUrl) { setSwatch(null); return; }
    const ctrl = new AbortController();
    // route through our proxy so the canvas isn't CORS-tainted
    const proxied = apiUrl(`/api/cover-proxy?url=${encodeURIComponent(coverUrl)}`);
    extractSwatch(proxied, ctrl.signal).then(setSwatch);
    return () => ctrl.abort();
  }, [coverUrl]);

  // apply --accent CSS variables on root
  useEffect(() => {
    const root = document.documentElement;
    const fallback = station?.accent || "#b8a6ff";
    if (swatch) {
      const hex = swatch.hex;
      root.style.setProperty("--accent", hex);
      root.style.setProperty(
        "--accent-soft",
        `hsla(${swatch.h.toFixed(1)} ${(swatch.s * 100).toFixed(0)}% ${(swatch.l * 100).toFixed(0)}% / 0.55)`,
      );
      root.style.setProperty(
        "--accent-glow",
        `hsla(${swatch.h.toFixed(1)} ${(swatch.s * 100).toFixed(0)}% ${(Math.max(0.45, swatch.l) * 100).toFixed(0)}% / 0.32)`,
      );
      root.style.setProperty(
        "--accent-deep",
        `hsl(${swatch.h.toFixed(1)} ${(swatch.s * 100).toFixed(0)}% ${(Math.max(0.25, swatch.l * 0.5) * 100).toFixed(0)}%)`,
      );
    } else {
      root.style.setProperty("--accent", fallback);
      root.style.setProperty("--accent-soft", `${fallback}8c`);
      root.style.setProperty("--accent-glow", `${fallback}47`);
    }
  }, [swatch, station?.accent]);

  const togglePlay = () => {
    if (!station && stations[0]) setStationId(stations[0].id);
    setPlaying((p) => !p);
  };
  const pick = (s: Station) => {
    setStationId(s.id);
    setPlaying(true);
    setPickerOpen(false);
  };
  const stepStation = (dir: 1 | -1) => {
    if (!stations.length) return;
    const i = stationId ? stations.findIndex((s) => s.id === stationId) : -1;
    const next = stations[(i + dir + stations.length) % stations.length];
    setStationId(next.id);
    setPlaying(true);
  };

  const accentHex = swatch?.hex || station?.accent || "#b8a6ff";

  return (
    <div className="app">
      <div className="map-layer">
        <MapView
          position={position}
          accentHex={accentHex}
          theme={theme.effective}
          satellite={satellite}
          routeGeometry={nav.state.route?.geometry ?? null}
          destination={nav.state.destination ? { lat: nav.state.destination.lat, lon: nav.state.destination.lon } : null}
          trafficMarkers={traffic}
        />
      </div>
      <div className="map-dim" />

      <div className="hud">
        <TopBar
          position={position}
          weather={weather}
          themeMode={theme.mode}
          onCycleTheme={theme.cycle}
          onOpenSearch={() => setSearchOpen(true)}
          onToggleDemoRoute={startDemoRoute}
          demoRouteActive={!!nav.state.route}
          satellite={satellite}
          onToggleSatellite={() => setSatellite((s) => !s)}
          onToggleDebug={() => setDebug((d) => !d)}
        />
        {(nav.state.route || nav.state.loading || nav.state.error) && (
          <NavStrip state={nav.state} onCancel={nav.cancelNavigation} />
        )}
        {nav.state.route && !IS_LITE && (() => {
          const curIdx = nav.state.progress?.currentStepIndex ?? 0;
          const steps = nav.state.route.steps;
          const currentStep = steps[curIdx] ?? null;
          const nextStep = steps[Math.min(curIdx + 1, steps.length - 1)] ?? null;
          return (
            <ManeuverInset
              currentStep={currentStep}
              nextStep={nextStep}
              distanceToManeuver={nav.state.progress?.distanceToNextManeuver ?? null}
              theme={theme.effective}
              accentHex={accentHex}
            />
          );
        })()}
        {debug && <DebugPanel position={position} limit={limit} roadError={roadError} onClose={() => setDebug(false)} />}
        <SpeedHud position={position} limit={limit} roadError={roadError} />
        <Player
          station={station}
          np={np}
          playing={playing}
          onTogglePlay={togglePlay}
          onOpenPicker={() => setPickerOpen(true)}
          onNext={() => stepStation(1)}
          onPrev={() => stepStation(-1)}
          audioRef={audioRef as React.RefObject<HTMLAudioElement>}
        />
      </div>

      {searchOpen && (
        <SearchPanel
          position={position}
          onPick={(dest) => { setSearchOpen(false); nav.startNavigation(dest); }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {pickerOpen && (
        <StationPicker
          stations={stations}
          activeId={stationId}
          favorites={favorites.ids}
          onToggleFavorite={favorites.toggle}
          onPick={pick}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* No crossOrigin — ICY MP3 endpoints (like DR's) don't ship CORS headers
          and only need plain media playback. hls.js handles CORS itself. */}
      <audio ref={audioRef} preload="none" />
    </div>
  );
}
